import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../../i18n/useT';
import './CookieBanner.css';

const DEFAULT_PREFERENCES = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export default function CookieBanner({ onAccept }) {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const t = useT();
  const c = t.cookie;

  useEffect(() => {
    const saved = localStorage.getItem('cookieConsent');
    if (!saved) setShowBanner(true);
  }, []);

  function handleAcceptAll() {
    save({ essential: true, functional: true, analytics: true, marketing: true });
  }

  function handleRejectNonEssential() {
    save(DEFAULT_PREFERENCES);
  }

  function handleSavePreferences() {
    save({ ...preferences, essential: true });
  }

  function save(prefs) {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    onAccept(prefs);
    setShowBanner(false);
  }

  function togglePref(key) {
    if (key === 'essential') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  }

  if (!showBanner) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-label="Cookie preferences" aria-modal="true">
      <div className="cookie-banner__inner">
        {!showDetails ? (
          <div className="cookie-banner__compact">
            <div className="cookie-banner__text">
              <p className="cookie-banner__title">{c.title}</p>
              <p className="cookie-banner__desc">
                {c.desc}{' '}
                <Link to="/cookie-policy">{t.footer.links.cookiePolicy}</Link> &amp;{' '}
                <Link to="/privacy">{t.footer.links.privacy}</Link>.
              </p>
            </div>
            <div className="cookie-banner__actions">
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDetails(true)}>{c.manage}</button>
              <button className="btn btn-secondary btn-sm" onClick={handleRejectNonEssential}>{c.reject}</button>
              <button className="btn btn-primary btn-sm" onClick={handleAcceptAll}>{c.acceptAll}</button>
            </div>
          </div>
        ) : (
          <div className="cookie-banner__details">
            <div className="cookie-banner__details-header">
              <p className="cookie-banner__title">{c.prefTitle}</p>
              <button className="cookie-banner__back" onClick={() => setShowDetails(false)} aria-label="Back">{c.back}</button>
            </div>
            <div className="cookie-banner__categories">
              <CookieCategory name={c.essential.name} description={c.essential.desc} checked={true} disabled={true} />
              <CookieCategory name={c.functional.name} description={c.functional.desc} checked={preferences.functional} onChange={() => togglePref('functional')} />
              <CookieCategory name={c.analytics.name} description={c.analytics.desc} checked={preferences.analytics} onChange={() => togglePref('analytics')} />
              <CookieCategory name={c.marketing.name} description={c.marketing.desc} checked={preferences.marketing} onChange={() => togglePref('marketing')} />
            </div>
            <div className="cookie-banner__actions">
              <button className="btn btn-secondary btn-sm" onClick={handleRejectNonEssential}>{c.reject}</button>
              <button className="btn btn-primary btn-sm" onClick={handleSavePreferences}>{c.save}</button>
              <button className="btn btn-primary btn-sm" onClick={handleAcceptAll}>{c.acceptAll}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CookieCategory({ name, description, checked, onChange, disabled }) {
  return (
    <div className="cookie-category">
      <div className="cookie-category__info">
        <p className="cookie-category__name">{name}</p>
        <p className="cookie-category__desc">{description}</p>
      </div>
      <label className={`toggle${disabled ? ' toggle--disabled' : ''}`} aria-label={name}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          aria-label={name}
        />
        <span className="toggle__track" />
      </label>
    </div>
  );
}
